import {Injectable, Optional} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {Address, B2bUnit, ContactFormData, Invitation, User} from '@kion/data-models';
import {HttpClient} from '@angular/common/http';
import {ApiConfig} from '../../../../../api-endpoint/src/lib/api.config';

interface IResponseItem {
  guid: string,
  uid: string
}

export interface TokenValidationResponse {
  emailAddress: string,
  firstName: string,
  lastName: string
}

type SearchResponse = IResponseItem[];

@Injectable({
  providedIn: 'root'
})
export class UserConnectors {

  private readonly _rootUrl: string;
  private readonly _baseSite: string;

  private route = '/users/';

  private accessRoute = '/access/';

  private b2bUnitRoute = '/b2bunits/';

  constructor(
    @Optional() apiConfig: ApiConfig,
    private http: HttpClient,
  ) {
    this._rootUrl = apiConfig.rootUrl;
    this._baseSite = apiConfig.baseSite;
  }

  private static transformFromApi(response: IResponseItem): User {
    return {
      ...response,
      id: response.guid
    };
  }

  inviteUser(invitation: Invitation): Observable<void> {
    return this.http.put(this.getInvitationUrl(), invitation).pipe(
      map(
        () => null
      ),
      catchError(error => throwError(error))
    );
  }

  inviteAdmin(invitation: Invitation): Observable<void> {
    const route = this._rootUrl + this._baseSite + this.accessRoute + 'invite/admin';
    return this.http.put(route, invitation).pipe(
      map(
        () => null
      ),
      catchError(error => throwError(error))
    );
  }

  validateInvitationToken(email: string, token: string): Observable<TokenValidationResponse> {
    return this.http.post<TokenValidationResponse>(
      this.getTokenValidationUrl(),
      {
        emailAddress: email,
        invitationToken: decodeURIComponent(token)
      }).pipe(map(() => null),
      catchError(error => throwError(error))
    )
  }

  validateInvitationTokenOnly(token: string): Observable<TokenValidationResponse> {
    return this.http.post<TokenValidationResponse>(
      this.getTokenValidationUrl(),
      {
        invitationToken: token
      }).pipe(
      map(
        (response: TokenValidationResponse) => {
          return response;
        }
      ),
      catchError(error => throwError(error))
    )
  }

  registerUser(
    accessToken: string,
    identityToken: string,
    firstName: string,
    lastName: string,
    email: string,
    idp: string,
    oid: string,
    address: Partial<Address>,
    invitationToken?: string
  ): Observable<void> {
    let body = {
      accessToken: accessToken,
      firstName: firstName,
      identityToken: identityToken,
      lastName: lastName,
      uid: email,
      idpUserFlow: idp,
      idpUserId: oid,
      defaultAddress: address
    };

    if (invitationToken) {
      body = {
        ...body,
        // @ts-ignore
        invitationToken: decodeURIComponent(invitationToken)
      }
    }
    return this.http.post<void>(this.getManyUrl(), body).pipe(
      map(
        () => null
      ),
      catchError(error => throwError(error))
    );
  }

  resendInvite(invitationId: string): Observable<void> {
    return this.http.put<void>(this.getResendUrl(invitationId), {}).pipe(
      map(
        () => null
      ),
      catchError(error => throwError(error))
    )
  }

  deleteInvite(invitationId: string): Observable<void> {
    return this.http.put<void>(this.getDeleteUrl(invitationId), {}).pipe(
       map(
          () => null
       ),
       catchError(error => throwError(error))
    )
  }

  loadUserById(): Observable<User> {
    const route: string = this._rootUrl + this._baseSite + '/users/current';
    return this.http.get<IResponseItem>(route).pipe(
      map(
        (response: IResponseItem) => UserConnectors.transformFromApi(response)
      ),
      catchError(
        error => throwError(error)
      )
    );
  }

  updateB2bUnit(b2bUnitId: string, registration: B2bUnit): Observable<void> {
    const route = this.getB2bUnitUrl(b2bUnitId);

    return this.http.put<void>(route, registration).pipe(
      map(() => null),
      catchError(error => throwError(error))
    )
  }

  sendContactForm(data: ContactFormData): Observable<string> {
    const route = this._rootUrl + this._baseSite + '/contact';
    return this.http.post(route, data).pipe(
      map(() => null),
      catchError(error => throwError(error))
    )
  }

  protected getInvitationUrl(): string {
    return this._rootUrl + this._baseSite + this.accessRoute + 'invite/user';
  }

  protected getTokenValidationUrl(): string {
    return this._rootUrl + this._baseSite + this.accessRoute + 'invite/token/validate';
  }

  protected getManyUrl() {
    return this._rootUrl + this._baseSite + this.route;
  }

  protected getOneUrl(id: number) {
    return this._rootUrl + this.route + id;
  }

  protected getResendUrl(invitationId: string): string {
    return this._rootUrl + this._baseSite + this.accessRoute + '/invite/' + invitationId + '/resend';
  }

  protected getDeleteUrl(invitationId: string): string {
    return this._rootUrl + this._baseSite + this.accessRoute + '/invite/' + invitationId + '/delete';
  }

  protected getB2bUnitUrl(b2bUnitId: string): string {
    return this._rootUrl + this._baseSite + this.b2bUnitRoute + b2bUnitId;
  }
}
